import React, { useCallback, useEffect, useState } from 'react';
import { MdArrowForward } from 'react-icons/md';
import { usePromiseTracker } from 'react-promise-tracker';
import { LoadingMenu } from '../../../../../../components/LoadingSpinner';
import SelectPill from '../../../../../../components/SelectPill';
import { useElasticidadeCruzadaStore } from '../../ElasticidadeCruzada.store';
import { searchLocations } from '../../services';
import type { FilterOption, LocationType } from '../../types';
import { regionValueMatchesOption } from '../../utils/locationRegionUtils';
import styles from './styles.module.scss';

const LOCATION_TYPE_OPTIONS = [
  { label: 'Todo o território', value: 'TODO_TERRITORIO' },
  { label: 'Região', value: 'REGIAO' },
];

export const LocationSelector = () => {
  const {
    produtoReferencia,
    produtosCompetidores,
    segmentos,
    subsegmentos,
    tipoLocalizacao,
    localizacao,
    setFilterValue,
    setFilterList,
    setFilterCache,
    setState,
  } = useElasticidadeCruzadaStore();
  const { promiseInProgress: isLoadingRegions } = usePromiseTracker({ area: 'elasticidade-regions' });

  const selectedProductBarcode = produtoReferencia.value?.[0] as string | undefined;
  const selectedCompetitorBarcode = produtosCompetidores.value?.[0] as string | undefined;
  const selectedSegmentation = segmentos.value?.[0] as string | undefined;
  const selectedSubSegmentation = subsegmentos.value?.[0] as string | undefined;

  const [regionsOptions, setRegionsOptions] = useState<FilterOption[]>([]);

  useEffect(() => {
    if (tipoLocalizacao !== 'REGIAO') {
      setRegionsOptions([]);
      return;
    }
    if (!selectedProductBarcode) {
      setRegionsOptions([]);
      return;
    }
    searchLocations({
      type: 'REGIAO',
      query: '',
      barcode: selectedProductBarcode,
      barcodeCompetitor: selectedCompetitorBarcode,
      segmentation: selectedSegmentation,
      subSegmentation: selectedSubSegmentation,
    }).then(regions => {
      setRegionsOptions(regions);
    });
  }, [tipoLocalizacao, selectedCompetitorBarcode, selectedProductBarcode, selectedSegmentation, selectedSubSegmentation]);

  useEffect(() => {
    if (tipoLocalizacao !== 'REGIAO') return;
    const current = localizacao.value?.[0] as string | undefined;
    if (!current || !regionsOptions.length) return;
    if (!regionValueMatchesOption(current, regionsOptions)) {
      setFilterValue('localizacao', []);
      setFilterList('localizacao', []);
      setFilterCache('localizacao', []);
    }
  }, [tipoLocalizacao, regionsOptions, localizacao.value, setFilterValue, setFilterList, setFilterCache]);

  const handleLocationTypeChange = useCallback(
    (value: LocationType) => {
      setState('tipoLocalizacao', value || 'TODO_TERRITORIO');
      setFilterValue('localizacao', []);
      setFilterList('localizacao', []);
      setFilterCache('localizacao', []);
    },
    [setFilterCache, setFilterList, setFilterValue, setState],
  );

  const handleLocationChange = useCallback(
    (value: string) => {
      setFilterValue('localizacao', value ? [value] : []);
    },
    [setFilterValue],
  );

  const handleLocationClean = useCallback(() => {
    setFilterValue('localizacao', []);
    setFilterCache('localizacao', []);
  }, [setFilterCache, setFilterValue]);

  return (
    <div className={styles.filterSelectionWrapper}>
      <span className={styles.filterLabel}>LOCAL</span>
      {/* @ts-ignore */}
      <SelectPill
        id="select-picker-pill"
        placeholder="Todo o território"
        data={LOCATION_TYPE_OPTIONS}
        value={tipoLocalizacao}
        onSelect={handleLocationTypeChange}
        searchable={false}
        cleanable={false}
      />
      {tipoLocalizacao === 'REGIAO' && (
        <>
          <MdArrowForward className={styles.arrowIcon} />
          {/* @ts-ignore */}
          <SelectPill
            id="select-picker-pill"
            placeholder="Selecione uma região"
            data={regionsOptions}
            value={localizacao.value?.[0] || null}
            onSelect={handleLocationChange}
            onClean={handleLocationClean}
            searchable={false}
            cleanable
            locale={{
              noResultsText: isLoadingRegions ? ' ' : 'Nenhum item encontrado',
              searchPlaceholder: 'Buscar item',
            }}
            renderMenu={(menu: React.ReactNode) => (
              <span>
                <LoadingMenu area="elasticidade-regions" />
                {menu}
              </span>
            )}
          />
        </>
      )}
    </div>
  );
};
